import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatBRL, formatDateTimeBR } from "../lib/api";
import { canCancelOperation } from "../lib/operations";
import {
  TrendingUp,
  Banknote,
  CheckCircle2,
  Users as UsersIcon,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  XCircle,
} from "lucide-react";
import StatusBadge from "../components/StatusBadge";
import CancelOperationDialog from "../components/CancelOperationDialog";
import { useAuth } from "../contexts/AuthContext";
import useCanCancelOperations from "../hooks/use-can-cancel-operations";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";
import { Skeleton } from "../components/ui/skeleton";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";

function KpiCard({ label, value, sub, icon: Icon, trend, testid }) {
  const isPositive = trend?.startsWith("+");
  return (
    <div className="zemo-card zemo-card-hover p-5 zemo-fade-up overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="zemo-overline">{label}</span>
        <div className="h-8 w-8 rounded-md bg-zinc-50 border border-[var(--zemo-border)] flex items-center justify-center flex-shrink-0">
          <Icon className="h-4 w-4 text-zinc-700" />
        </div>
      </div>
      <div className="mt-3 text-[22px] sm:text-[26px] lg:text-[28px] font-semibold tracking-tight text-zinc-900 font-mono-num truncate">
        {value}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[12px]">
        {trend && (
          <span
            className={`inline-flex items-center gap-0.5 font-semibold ${
              isPositive ? "text-emerald-600" : "text-red-600"
            }`}
          >
            {isPositive ? (
              <ArrowUpRight className="h-3 w-3" />
            ) : (
              <ArrowDownRight className="h-3 w-3" />
            )}
            {trend}
          </span>
        )}
        <span className="text-zinc-500 truncate">{sub}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  // Cancelamento nas "Recentes" (item 23b-iii): mesmos gates da lista de
  // Operações — papel admin + parceiro habilitado + gate da V2 por linha.
  const canCancel = user?.role === "admin";
  const canCancelOps = useCanCancelOperations(canCancel);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [byProduct, setByProduct] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const loadData = useCallback(async (showRefresh) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [m, t, bp, r] = await Promise.all([
        api.get("/dashboard/metrics"),
        api.get("/dashboard/timeline"),
        api.get("/dashboard/by-product"),
        api.get("/dashboard/recent-operations"),
      ]);
      setMetrics(m.data);
      setTimeline(t.data);
      setByProduct(bp.data);
      setRecent(r.data);
      setLastUpdate(new Date());
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadData(false); }, [loadData]);

  const timelineFormatted = useMemo(
    () =>
      timeline.map((d) => ({
        ...d,
        dia: new Date(d.date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }),
      })),
    [timeline]
  );

  const chartMargin = useMemo(() => ({ top: 10, right: 8, left: 0, bottom: 0 }), []);
  const barChartMargin = useMemo(() => ({ top: 10, right: 16, left: 0, bottom: 0 }), []);
  const tooltipStyle = useMemo(() => ({
    background: "#0a0a0a",
    border: "none",
    borderRadius: 8,
    color: "white",
    fontSize: 12,
  }), []);
  const tooltipLabelStyle = useMemo(() => ({ color: "#a1a1aa" }), []);

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Visão Geral</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">
            Dashboard de Produção
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Monitoramento em tempo real das operações e indicadores do parceiro.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[12px] text-zinc-500">
            Atualizado em <span className="font-semibold text-zinc-700">{lastUpdate.toLocaleString("pt-BR")}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="h-8"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Atualizando…" : "Atualizar"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading || !metrics ? (
          [...Array(4)].map((_, i) => <Skeleton key={`skeleton-${i}`} className="h-32 rounded-lg" />)
        ) : (
          <>
            <KpiCard
              testid="kpi-volume"
              label="Volume Total"
              value={formatBRL(metrics.volume_total)}
              sub={`últimos 30d ${formatBRL(metrics.volume_30d)}`}
              trend={`${metrics.growth_pct >= 0 ? "+" : ""}${metrics.growth_pct}%`}
              icon={Banknote}
            />
            <KpiCard
              testid="kpi-operacoes"
              label="Operações"
              value={metrics.total_operacoes.toLocaleString("pt-BR")}
              sub={`${metrics.operacoes_30d} nos últimos 30d`}
              icon={TrendingUp}
            />
            <KpiCard
              testid="kpi-aprovacao"
              label="Taxa de Aprovação"
              value={`${metrics.taxa_aprovacao}%`}
              sub="aprovadas + liquidadas"
              icon={CheckCircle2}
            />
            <KpiCard
              testid="kpi-cedentes"
              label="Cedentes Ativos"
              value={metrics.cedentes_ativos.toLocaleString("pt-BR")}
              sub="base do parceiro"
              icon={UsersIcon}
            />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 zemo-card p-5" data-testid="timeline-chart-card">
          <div className="flex items-center justify-between mb-1">
            <div>
              <span className="zemo-overline">Produção 30 dias</span>
              <h3 className="mt-1 text-lg font-semibold tracking-tight">Volume diário</h3>
            </div>
          </div>
          <div className="h-[280px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timelineFormatted} margin={chartMargin}>
                <defs>
                  <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0055FF" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#0055FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#f0f0f1" vertical={false} />
                <XAxis
                  dataKey="dia"
                  stroke="#a1a1aa"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  interval={3}
                />
                <YAxis
                  stroke="#a1a1aa"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(v) => formatBRL(v)}
                />
                <Area
                  type="monotone"
                  dataKey="volume"
                  stroke="#0055FF"
                  strokeWidth={2}
                  fill="url(#volGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* By product */}
        <div className="zemo-card p-5" data-testid="byproduct-chart-card">
          <span className="zemo-overline">Por Produto</span>
          <h3 className="mt-1 text-lg font-semibold tracking-tight">Distribuição</h3>
          <div className="h-[280px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byProduct} layout="vertical" margin={barChartMargin}>
                <CartesianGrid stroke="#f0f0f1" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#a1a1aa"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                />
                <YAxis
                  type="category"
                  dataKey="product_nome"
                  stroke="#52525b"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={100}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v) => formatBRL(v)}
                />
                <Bar dataKey="volume" fill="#0055FF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent operations */}
      <div className="zemo-card overflow-hidden" data-testid="recent-operations-card">
        <div className="px-5 py-4 border-b border-[var(--zemo-border)] flex items-center justify-between">
          <div>
            <span className="zemo-overline">Atividade</span>
            <h3 className="mt-1 text-lg font-semibold tracking-tight">Operações recentes</h3>
          </div>
          <Link
            to="/operacoes"
            className="text-[13px] text-[#0055FF] font-semibold hover:underline"
            data-testid="dashboard-view-all-ops"
          >
            Ver todas →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/50 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Número</th>
                <th className="text-left px-5 py-3 font-semibold">Cedente</th>
                <th className="text-left px-5 py-3 font-semibold">Produto</th>
                <th className="text-right px-5 py-3 font-semibold">Valor de Face</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Criada em</th>
                <th className="text-right px-5 py-3 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--zemo-border)]">
              {recent.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-zinc-500">
                    Nenhuma operação ainda.
                  </td>
                </tr>
              )}
              {recent.map((op) => (
                <tr key={op.id} className="hover:bg-zinc-50/60 transition-colors">
                  <td className="px-5 py-3.5 font-mono-num font-semibold text-zinc-900">{op.numero}</td>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-zinc-900">{op.cedente_nome}</div>
                    <div className="text-[11px] text-zinc-500 font-mono-num">{op.cedente_cnpj}</div>
                  </td>
                  <td className="px-5 py-3.5 text-zinc-700">{op.product_nome}</td>
                  <td className="px-5 py-3.5 text-right font-semibold text-zinc-900 font-mono-num">
                    {formatBRL(op.valor)}
                  </td>
                  <td className="px-5 py-3.5">
                    <StatusBadge status={op.status} />
                  </td>
                  <td className="px-5 py-3.5 text-[12px] text-zinc-500">{formatDateTimeBR(op.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    {/* Gate repassado da V2 via banco local (is_v2_synced +
                        v2_status WAITING_APPROVAL); órfã nunca oferece cancelar. */}
                    {canCancel && canCancelOps && canCancelOperation(op) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setCancelTarget(op)}
                        data-testid={`dash-cancel-op-${op.numero}`}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Cancelar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Diálogo de cancelamento compartilhado (Operações + Dashboard) */}
      <CancelOperationDialog
        operation={cancelTarget}
        onClose={() => setCancelTarget(null)}
        onCancelled={() => loadData(true)}
      />
    </div>
  );
}
